/**
 * Panneau d'audit global des modifications de notes (devoirs + examens).
 *
 * Permet à un admin de voir QUI a modifié QUOI sur QUELLE matière, avec
 * filtres par utilisateur, plage de dates, type d'opération + export CSV.
 *
 * Combine deux sources :
 *   - /historique-modifications-notes/  (devoirs)
 *   - /historique-modifications-notes-examens/  (examens)
 */
import React, { useMemo, useState } from 'react';
import {
  X, Clock, User, Filter, Download, Loader, ArrowRight, FileText,
  BookOpen, Award, RefreshCw,
} from 'lucide-react';
import { useGetHistoriqueQuery as useGetHistoriqueDevoirsQuery } from '../../services/notesDevoirsApi';
import { useGetHistoriqueQuery as useGetHistoriqueExamensQuery } from '../../services/notesExamensApi';
import { useGetUtilisateursQuery } from '../../services/coreApi';

interface Props {
  open: boolean;
  onClose: () => void;
}

interface UnifiedRow {
  rowId: string;
  source: 'devoir' | 'examen';
  date: string;
  modifie_par_nom: string | null;
  modifie_par_id: number | null;
  matiere_nom: string;
  etudiant_matricule: string;
  champ_modifie: string;
  ancienne_valeur: string | null;
  nouvelle_valeur: string | null;
  motif: string;
  motif_display: string;
  commentaire: string;
}

const CHAMP_LABEL: Record<string, string> = {
  note1: 'D1', note2: 'D2',
  absent1: 'AB-D1', absent2: 'AB-D2',
  note_examen: 'Examen', absent: 'AB-Examen',
};

const formatVal = (v: string | null, isAbsent: boolean): string => {
  if (v === null || v === '') return '—';
  if (isAbsent) return v === 'True' ? 'AB' : '—';
  return v;
};

const AuditNotesModal: React.FC<Props> = ({ open, onClose }) => {
  const [dateMin, setDateMin] = useState('');
  const [dateMax, setDateMax] = useState('');
  const [userId, setUserId] = useState<number | ''>('');
  const [searchEtuMat, setSearchEtuMat] = useState('');
  const [sourceFilter, setSourceFilter] = useState<'tous' | 'devoir' | 'examen'>('tous');
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 50;

  const { data: utilisateurs = [] } = useGetUtilisateursQuery(undefined, { skip: !open });

  // Buidler params : note_devoir_id n'est pas appliqué (audit global)
  const params = useMemo(() => {
    const p: any = {};
    if (userId) p.modifie_par = userId;
    if (dateMin) p.date_debut = dateMin;
    if (dateMax) {
      // inclusif : ajoute 23:59:59 pour englober toute la journée
      p.date_fin = dateMax + 'T23:59:59';
    }
    return p;
  }, [userId, dateMin, dateMax]);

  const { data: histDevoirs = [], isFetching: loadingD, refetch: refetchD } =
    useGetHistoriqueDevoirsQuery(params, { skip: !open });
  const { data: histExamens = [], isFetching: loadingE, refetch: refetchE } =
    useGetHistoriqueExamensQuery(params, { skip: !open });

  const allRows: UnifiedRow[] = useMemo(() => {
    const rows: UnifiedRow[] = [];
    histDevoirs.forEach(h => rows.push({
      rowId: `D-${h.id}`, source: 'devoir',
      date: h.date_modification,
      modifie_par_nom: h.modifie_par_nom, modifie_par_id: h.modifie_par,
      matiere_nom: h.matiere_nom,
      etudiant_matricule: h.etudiant_matricule,
      champ_modifie: h.champ_modifie,
      ancienne_valeur: h.ancienne_valeur,
      nouvelle_valeur: h.nouvelle_valeur,
      motif: h.motif, motif_display: h.motif_display,
      commentaire: h.commentaire,
    }));
    histExamens.forEach(h => rows.push({
      rowId: `E-${h.id}`, source: 'examen',
      date: h.date_modification,
      modifie_par_nom: h.modifie_par_nom, modifie_par_id: h.modifie_par,
      matiere_nom: h.matiere_nom,
      etudiant_matricule: h.etudiant_matricule,
      champ_modifie: h.champ_modifie,
      ancienne_valeur: h.ancienne_valeur,
      nouvelle_valeur: h.nouvelle_valeur,
      motif: h.motif, motif_display: h.motif_display,
      commentaire: h.commentaire,
    }));
    rows.sort((a, b) => b.date.localeCompare(a.date));
    return rows;
  }, [histDevoirs, histExamens]);

  // Filtre client : source + recherche libre matricule/matière
  const filteredRows = useMemo(() => {
    let out = allRows;
    if (sourceFilter !== 'tous') {
      out = out.filter(r => r.source === sourceFilter);
    }
    if (searchEtuMat.trim()) {
      const q = searchEtuMat.trim().toLowerCase();
      out = out.filter(r =>
        r.etudiant_matricule.toLowerCase().includes(q) ||
        r.matiere_nom.toLowerCase().includes(q),
      );
    }
    return out;
  }, [allRows, sourceFilter, searchEtuMat]);

  const totalPages = Math.max(1, Math.ceil(filteredRows.length / PAGE_SIZE));
  const pagedRows = filteredRows.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  // Stats par utilisateur
  const statsParUser = useMemo(() => {
    const map = new Map<string, number>();
    for (const r of filteredRows) {
      const k = r.modifie_par_nom || '— inconnu —';
      map.set(k, (map.get(k) || 0) + 1);
    }
    return Array.from(map.entries()).sort((a, b) => b[1] - a[1]).slice(0, 10);
  }, [filteredRows]);

  const exportCSV = () => {
    const cols = [
      'date', 'utilisateur', 'source', 'matricule_etudiant',
      'matiere', 'champ', 'ancien', 'nouveau', 'motif', 'commentaire',
    ];
    const lines = [
      cols.join(';'),
      ...filteredRows.map(r => [
        new Date(r.date).toLocaleString('fr-FR'),
        r.modifie_par_nom || '',
        r.source,
        r.etudiant_matricule,
        r.matiere_nom,
        CHAMP_LABEL[r.champ_modifie] || r.champ_modifie,
        r.ancienne_valeur ?? '',
        r.nouvelle_valeur ?? '',
        r.motif_display || r.motif,
        r.commentaire || '',
      ].map(v => String(v).replace(/[;\n\r]/g, ' ')).join(';')),
    ];
    const csv = '﻿' + lines.join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    const ts = new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-');
    a.href = url;
    a.download = `audit_modifications_notes_${ts}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const loading = loadingD || loadingE;

  if (!open) return null;

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-6xl w-full max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="px-5 py-3 border-b dark:border-gray-700 flex items-center justify-between">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2">
            <Clock size={20} className="text-indigo-600" />
            Audit — Historique des modifications de notes
          </h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X size={22} />
          </button>
        </div>

        {/* Filtres */}
        <div className="px-5 py-3 border-b dark:border-gray-700 bg-gray-50 dark:bg-gray-900/30 flex items-center gap-3 flex-wrap text-xs">
          <div className="flex items-center gap-1.5 font-medium text-gray-600 dark:text-gray-400">
            <Filter size={12} /> Filtres :
          </div>

          <select
            value={userId}
            onChange={(e) => { setPage(1); setUserId(e.target.value ? parseInt(e.target.value) : ''); }}
            className="px-2 py-1 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700"
          >
            <option value="">Tous utilisateurs</option>
            {utilisateurs.filter(u => u.actif).map(u => (
              <option key={u.id} value={u.id}>{u.nom} {u.prenoms} ({u.role})</option>
            ))}
          </select>

          <select
            value={sourceFilter}
            onChange={(e) => { setPage(1); setSourceFilter(e.target.value as any); }}
            className="px-2 py-1 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700"
          >
            <option value="tous">Tous types</option>
            <option value="devoir">Devoirs uniquement</option>
            <option value="examen">Examens uniquement</option>
          </select>

          <div className="flex items-center gap-1">
            <span className="text-gray-600 dark:text-gray-400">Du</span>
            <input
              type="date"
              value={dateMin}
              onChange={(e) => { setPage(1); setDateMin(e.target.value); }}
              className="px-2 py-1 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700"
            />
            <span className="text-gray-600 dark:text-gray-400">au</span>
            <input
              type="date"
              value={dateMax}
              onChange={(e) => { setPage(1); setDateMax(e.target.value); }}
              className="px-2 py-1 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700"
            />
          </div>

          <input
            type="text"
            value={searchEtuMat}
            onChange={(e) => { setPage(1); setSearchEtuMat(e.target.value); }}
            placeholder="Matricule ou matière…"
            className="flex-1 min-w-[160px] px-2 py-1 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700"
          />

          <button
            onClick={() => { refetchD(); refetchE(); }}
            className="flex items-center gap-1 px-2 py-1 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded hover:bg-gray-100"
          >
            <RefreshCw size={12} className={loading ? 'animate-spin' : ''} /> Actualiser
          </button>
          <button
            onClick={exportCSV}
            disabled={filteredRows.length === 0}
            className="flex items-center gap-1 px-2 py-1 bg-emerald-600 hover:bg-emerald-700 text-white rounded disabled:opacity-50"
          >
            <Download size={12} /> Export CSV
          </button>
        </div>

        {/* Stats par utilisateur (top 10) */}
        {statsParUser.length > 0 && (
          <div className="px-5 py-2 border-b dark:border-gray-700 bg-indigo-50/30 dark:bg-indigo-900/10 flex items-center gap-2 flex-wrap text-xs">
            <span className="font-semibold text-indigo-700 dark:text-indigo-300">Top 10 utilisateurs :</span>
            {statsParUser.map(([nom, count]) => (
              <span
                key={nom}
                className="px-2 py-0.5 bg-white dark:bg-gray-700 border border-indigo-200 dark:border-indigo-800 rounded"
              >
                <span className="font-medium">{nom}</span>{' '}
                <span className="text-indigo-700 dark:text-indigo-300 font-bold">{count}</span>
              </span>
            ))}
          </div>
        )}

        {/* Tableau */}
        <div className="flex-1 overflow-auto">
          {loading ? (
            <div className="text-center py-8">
              <Loader className="animate-spin mx-auto text-indigo-600" size={28} />
              <p className="mt-2 text-sm text-gray-500">Chargement…</p>
            </div>
          ) : filteredRows.length === 0 ? (
            <div className="text-center py-12 text-sm text-gray-500">
              Aucune modification trouvée avec ces filtres.
            </div>
          ) : (
            <table className="w-full text-xs">
              <thead className="sticky top-0 bg-gray-50 dark:bg-gray-700/80 backdrop-blur z-10">
                <tr className="text-[10px] uppercase tracking-wide text-gray-600 dark:text-gray-400">
                  <th className="px-2 py-2 text-left">Date</th>
                  <th className="px-2 py-2 text-left">Utilisateur</th>
                  <th className="px-1 py-2 text-center">Type</th>
                  <th className="px-2 py-2 text-left">Matricule</th>
                  <th className="px-2 py-2 text-left">Matière</th>
                  <th className="px-2 py-2 text-center">Champ</th>
                  <th className="px-2 py-2 text-center">Ancien → Nouveau</th>
                  <th className="px-2 py-2 text-left">Motif</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                {pagedRows.map(r => {
                  const isAbsent = r.champ_modifie.startsWith('absent');
                  return (
                    <tr key={r.rowId} className="hover:bg-gray-50 dark:hover:bg-gray-700/30">
                      <td className="px-2 py-1 text-gray-600 dark:text-gray-400 font-mono">
                        {new Date(r.date).toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' })}
                      </td>
                      <td className="px-2 py-1 text-gray-800 dark:text-gray-200">
                        <span className="flex items-center gap-1">
                          <User size={10} className="text-gray-400" />
                          {r.modifie_par_nom || <em className="text-gray-400">—</em>}
                        </span>
                      </td>
                      <td className="px-1 py-1 text-center">
                        {r.source === 'devoir' ? (
                          <span className="inline-flex items-center gap-0.5 px-1 py-0.5 bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300 rounded text-[10px] font-semibold">
                            <BookOpen size={9} />Dev
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-0.5 px-1 py-0.5 bg-emerald-100 dark:bg-emerald-900/40 text-emerald-700 dark:text-emerald-300 rounded text-[10px] font-semibold">
                            <Award size={9} />Ex
                          </span>
                        )}
                      </td>
                      <td className="px-2 py-1 font-mono text-gray-700 dark:text-gray-300">{r.etudiant_matricule}</td>
                      <td className="px-2 py-1 text-gray-700 dark:text-gray-300 max-w-[180px] truncate" title={r.matiere_nom}>
                        {r.matiere_nom}
                      </td>
                      <td className="px-2 py-1 text-center font-semibold text-gray-700 dark:text-gray-300">
                        {CHAMP_LABEL[r.champ_modifie] || r.champ_modifie}
                      </td>
                      <td className="px-2 py-1 text-center">
                        <span className="inline-flex items-center gap-1">
                          <span className="px-1.5 py-0.5 bg-gray-100 dark:bg-gray-700 rounded text-gray-700 dark:text-gray-300 font-mono">
                            {formatVal(r.ancienne_valeur, isAbsent)}
                          </span>
                          <ArrowRight size={10} className="text-gray-400" />
                          <span className="px-1.5 py-0.5 bg-blue-100 dark:bg-blue-900/40 text-blue-800 dark:text-blue-200 rounded font-mono font-semibold">
                            {formatVal(r.nouvelle_valeur, isAbsent)}
                          </span>
                        </span>
                      </td>
                      <td className="px-2 py-1 text-gray-600 dark:text-gray-400">
                        <span className="text-[10px] px-1.5 py-0.5 bg-gray-100 dark:bg-gray-700 rounded">
                          {r.motif_display || r.motif || '—'}
                        </span>
                        {r.commentaire && (
                          <span title={r.commentaire} className="ml-1 text-gray-400 cursor-help">
                            <FileText size={10} className="inline" />
                          </span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        {/* Footer + pagination */}
        <div className="px-5 py-2.5 border-t dark:border-gray-700 flex justify-between items-center text-xs">
          <span className="text-gray-500 dark:text-gray-400">
            {filteredRows.length === 0
              ? '0 modification'
              : `${filteredRows.length} modification${filteredRows.length > 1 ? 's' : ''} · page ${page}/${totalPages}`}
          </span>
          <div className="flex items-center gap-2">
            {totalPages > 1 && (
              <>
                <button
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={page === 1}
                  className="px-2 py-1 bg-gray-200 dark:bg-gray-700 rounded disabled:opacity-50"
                >
                  ← Précédent
                </button>
                <button
                  onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                  className="px-2 py-1 bg-gray-200 dark:bg-gray-700 rounded disabled:opacity-50"
                >
                  Suivant →
                </button>
              </>
            )}
            <button
              onClick={onClose}
              className="px-3 py-1 bg-gray-300 dark:bg-gray-600 text-gray-800 dark:text-gray-200 rounded hover:bg-gray-400"
            >
              Fermer
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AuditNotesModal;
